import React, { useMemo } from 'react'
import { isArray } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Icon, toast } from '@node-space/ui-components'
import { useCreateCustomerDocumentLink } from 'hooks/mutations/useCreateCustomerDocumentLink'
import { CustomerDocumentStatusHighlight } from 'pages/Customers/components/CustomerHighlight'
import { openInNewTab } from 'utils/openInNewTab'
import { COMPANY_REQUIRED_DOCUMENTS, INDIVIDUAL_REQUIRED_DOCUMENTS } from '../helpers/constants'
import type { CustomerDocument, RequiredDocument } from '../helpers/types'
import { useMergedDocuments } from '../hooks/useMergedDocuments'

interface CustomerDocumentsProps {
  documents: CustomerDocument[]
  isCompany: boolean
}

export const CustomerDocuments = ({ documents, isCompany }: CustomerDocumentsProps) => {
  const { t } = useTranslation()

  const { mutate: createCustomerDocumentLink } = useCreateCustomerDocumentLink()

  const { mergedDocuments } = useMergedDocuments(documents, isCompany)

  const buildRequiredNameMap = (documents: RequiredDocument[]) =>
    documents.reduce<Record<string, string>>((map, document) => {
      const typeKeys = isArray(document?.type) ? document?.type : [document?.type]

      const keys: string[] = [...(document?.subType ? [document?.subType] : []), ...typeKeys]
      const effectiveKeys = keys.length > 0 ? keys : [document?.nameKey]
      effectiveKeys.forEach(key => {
        map[key] = t(`embeddedCustomers.requiredDocuments.${document?.nameKey}`)
      })
      return map
    }, {})

  const requiredDocName = useMemo(() => {
    const documents = isCompany ? COMPANY_REQUIRED_DOCUMENTS : INDIVIDUAL_REQUIRED_DOCUMENTS
    return buildRequiredNameMap(documents)
  }, [isCompany])

  const handleDocumentLink = (document: CustomerDocument) => {
    createCustomerDocumentLink(document?.reference, {
      onSuccess: data => {
        if (data) {
          openInNewTab(data)
          toast({
            header: t('embeddedCustomers.documentDownloadToast.success'),
            variant: 'success',
            body: `${document?.name}`,
          })
        }
      },
      onError: () => {
        toast({
          header: t('embeddedCustomers.documentDownloadToast.error'),
          variant: 'error',
        })
      },
    })
  }

  const getDocumentElement = (document: CustomerDocument & { isUploaded?: boolean }) => {
    if (!document?.isUploaded) return null
    if (isCompany) {
      return (
        <Button
          testId="download-document"
          iconElement={<Icon name="DownloadSimple" />}
          onClick={() => handleDocumentLink(document)}
          variant="ghost"
        >
          {document?.name}
        </Button>
      )
    }
    return <span className="text-body-regular text-foreground-subdued ml-3">{document?.name}</span>
  }

  return (
    <Box>
      {mergedDocuments?.map((document, idx) => {
        const isUploaded = document?.isUploaded
        const docType = document?.type
        const requiredKey = isCompany ? document?.subType : docType
        const displayName =
          docType === 'OTHER'
            ? t('embeddedCustomers.requiredDocuments.other')
            : (requiredDocName?.[requiredKey] ?? '')

        const rowKey = isUploaded ? document?.reference : requiredKey

        return (
          <Box key={rowKey}>
            <DataListItem
              layout="horizontal"
              body=""
              title={`${idx + 1}. ${displayName}`}
              id={rowKey}
              testId="customer-document"
              HeaderHighlight={
                <CustomerDocumentStatusHighlight
                  status={document?.status}
                  isUploaded={isUploaded}
                />
              }
            />
            {getDocumentElement(document)}
          </Box>
        )
      })}
    </Box>
  )
}
